import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Banknote } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { LEDGERS } from "@/constants/testIds";

/**
 * Staff-loan ledger for one employee. Admin only. Running balance visible.
 * Repayments applied at pay-run as POST-tax deductions.
 */
export default function LoanLedgerSection({ employee, onChanged }) {
    const entries = (employee?.loan_ledger || []).slice().sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    const [balance, setBalance] = useState(null);
    const [type, setType] = useState("advance");
    const [date, setDate] = useState("");
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const loadBalance = async () => {
        try {
            const { data } = await api.get(`/employees/${employee.id}/loan-balance`);
            setBalance(data.outstanding_kina);
        } catch (ex) {
            setBalance(null);
        }
    };

    useEffect(() => { loadBalance(); }, [employee?.id, employee?.loan_ledger?.length]);

    const add = async () => {
        if (!date || !amount) return;
        setBusy(true);
        try {
            await api.post(`/employees/${employee.id}/loan-ledger`, {
                type, date, amount_kina: Number(amount), note,
            });
            setDate(""); setAmount(""); setNote(""); setType("advance");
            onChanged?.();
            loadBalance();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Add failed");
        } finally { setBusy(false); }
    };

    return (
        <section data-testid={LEDGERS.loanSection} className="mt-6 border border-neutral-200 rounded-[3px] p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Banknote size={14} className="text-[color:var(--kp-red)]" />
                    <div className="text-[11px] uppercase tracking-[0.18em] font-display font-semibold text-[color:var(--kp-charcoal)]">
                        Staff loan
                    </div>
                </div>
                <div data-testid={LEDGERS.loanBalance}
                     className="text-[13px] font-mono-money">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display mr-2">
                        Outstanding
                    </span>
                    <strong className={balance > 0 ? "text-[color:var(--kp-red)]" : "text-neutral-500"}>
                        K{balance === null ? "…" : Number(balance).toFixed(2)}
                    </strong>
                </div>
            </div>

            <div className="grid grid-cols-[110px_130px_120px_1fr_auto] gap-2 items-start mb-3">
                <select data-testid={LEDGERS.loanAddType} value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="kp-focus border border-neutral-300 px-2 py-1.5 text-[12px] rounded-[2px]">
                    <option value="advance">Advance</option>
                    <option value="repayment">Repayment</option>
                </select>
                <input data-testid={LEDGERS.loanAddDate} type="date" value={date}
                       onChange={(e) => setDate(e.target.value)}
                       className="kp-focus border border-neutral-300 px-2 py-1.5 text-[12px] font-mono-money rounded-[2px]" />
                <input data-testid={LEDGERS.loanAddAmount} type="number" step="0.01" placeholder="Kina"
                       value={amount} onChange={(e) => setAmount(e.target.value)}
                       className="kp-focus border border-neutral-300 px-2 py-1.5 text-[12px] font-mono-money rounded-[2px]" />
                <input data-testid={LEDGERS.loanAddNote} placeholder="Note"
                       value={note} onChange={(e) => setNote(e.target.value)}
                       className="kp-focus border border-neutral-300 px-2 py-1.5 text-[12px] rounded-[2px]" />
                <button data-testid={LEDGERS.loanAddBtn} onClick={add}
                        disabled={busy || !date || !amount}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] font-display font-semibold text-white rounded-[2px] disabled:opacity-40"
                        style={{ background: "var(--kp-red)" }}>
                    <Plus size={12} /> Add
                </button>
            </div>

            {entries.length === 0 ? (
                <div className="text-[11px] text-neutral-400 italic py-4">No loan history yet.</div>
            ) : (
                <table className="w-full text-[12px]">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-[0.12em] text-neutral-500 font-display">
                            <th className="text-left py-1 px-2 font-semibold">Date</th>
                            <th className="text-left py-1 px-2 font-semibold">Type</th>
                            <th className="text-right py-1 px-2 font-semibold">Amount</th>
                            <th className="text-left py-1 px-2 font-semibold">Note</th>
                            <th className="text-left py-1 px-2 font-semibold">Recorded by</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((e) => (
                            <tr key={e.id} data-testid={LEDGERS.loanRow(e.id)} className="border-t border-neutral-100">
                                <td className="py-1.5 px-2 font-mono-money">{e.date}</td>
                                <td className="py-1.5 px-2">
                                    <span className={`text-[10px] uppercase tracking-[0.14em] font-display font-semibold ${e.type === "advance" ? "text-[color:var(--kp-red)]" : "text-[color:var(--kp-success)]"}`}>
                                        {e.type}
                                    </span>
                                </td>
                                <td className="py-1.5 px-2 text-right font-mono-money">K{Number(e.amount_kina).toFixed(2)}</td>
                                <td className="py-1.5 px-2 text-neutral-600">{e.note || "—"}</td>
                                <td className="py-1.5 px-2 text-[10px] text-neutral-500">{e.recorded_by || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
}
